import { MAX_REPS } from "../constants/exercises";
import { coefficients } from "../constants/repCoefficients";
import { EMPTY_EXERCISE } from "../constants/templates";
import {
  PB_DEFAULT_VALUE,
  MAX_PB_COEFFICIENT,
  MIN_PB_COEFFICIENT,
  PB_STEP_THRESHOLD,
  STEP_SMALL,
  STEP_BIG,
} from "../constants/training";
import {
  NO_WORKOUT_SCROLL_OFFSET,
  WORKOUT_SCROLL_OFFSET_TOP_M,
  WORKOUT_SCROLL_OFFSET_TOP_L,
} from "../pages/Clients/ClientTrainingPage/const";

import {
  BasicHelper,
  getUpdatedArray,
  groupByField,
  incToCondition,
} from "./index";

/**
 * Calculate number of goal repetitions
 * @param {number} pb personal best
 * @param {number} weight
 * @param {string} repCalculator type of the coefficients to use
 * @returns {number} repetitions
 */
export const calculateGoalReps = (pb, weight, repCalculator) => {
  const maxReps = coefficients[repCalculator]?.length ?? 0;
  if (!pb) return maxReps;
  return incToCondition(
    (reps) => reps >= maxReps || pb < calculateMax(weight, reps, repCalculator)
  );
};

export const getMergedId = (exerciseId, setId) => `${exerciseId}-${setId}`;

export const roundMax = (max) => BasicHelper.roundTo(max, max < 100);

export const stepCalculate = (pb) =>
  pb <= PB_STEP_THRESHOLD ? STEP_SMALL : STEP_BIG;

export const calculateNearestMultipleOfNumber = (number, multiple) =>
  Math.round(number / multiple) * multiple;

export const calculateGoalWeight = (pb, reps, repCalculator) => {
  const notNullPb = pb || PB_DEFAULT_VALUE;
  const step = stepCalculate(notNullPb);
  const maxWeight = notNullPb * MAX_PB_COEFFICIENT;
  const minWeight = notNullPb * MIN_PB_COEFFICIENT;
  const start = calculateNearestMultipleOfNumber(minWeight, step);

  return incToCondition(
    (weight) =>
      weight >= maxWeight ||
      notNullPb < calculateMax(weight, reps, repCalculator),
    start,
    step
  );
};

/**
 *
 * @param {number} weight
 * @param {number} reps number of repetitions
 * @param {string} repCalculator type of the coefficients to use
 * @returns {number} calculated max
 */
export const calculateMax = (weight, reps, repCalculator) => {
  // const repCalc =
  //   repCalculator === "legPressExtension"
  //     ? REP_CALCULATOR_TYPES.LEG_PRESS_EXT
  //     : repCalculator;
  // const coeff = coefficients[repCalc][reps - 1];
  if (coefficients[repCalculator] && reps > 0) {
    const coeff = coefficients[repCalculator][reps - 1];
    const value = weight * coeff;
    return roundMax(value) || 0;
  }
  return 0;
};

/**
 * @param {number} currentPb current personal best
 * @param {number} weight weight as a percentage
 * @returns calculated current weight
 */
export const calculateWeight = (currentPb, weight) => {
  const pb = currentPb || PB_DEFAULT_VALUE;
  return Math.round((pb * weight) / 100);
};

/**
 *
 * @param {object} currentExercise
 * @param {number} currentSetIdx
 * @param {object} currentSet
 * @param {number} pb
 * @param {boolean} pbIsCalculated
 * @param {Object} template
 * @return {ComposedSet: Object}
 */
export const composeSetValues = (
  currentExercise,
  currentSetIdx,
  currentSet,
  pb,
  pbIsCalculated,
  template,
  isNewProgramAdded
) => {
  const defaultSet = {
    weight: 20,
    reps: null,
    completed_at: null,
    notes: null,
    other_reps: 0,
    other_reps_notes: null,
    is_completed: false,
  };

  const defaultExercise = {
    day_index: 0,
    workout_index: 0,
    exercise_index: 0,
    rep_calculator: "Standard",
    exercise_name: "unspecified",
    exercise_id: 0,
  };

  const _currentSet = currentSet || defaultSet;
  const _currentExercise = currentExercise || defaultExercise;

  const {
    weight,
    reps,
    completed_at,
    notes,
    other_reps,
    other_reps_notes,
    is_completed,
    goal_reps,
  } = _currentSet;

  const {
    day_index,
    workout_index,
    exercise_index,
    rep_calculator,
    exercise_name,
    exercise_id,
  } = _currentExercise;

  const set = template?.exercises?.find((exercise) =>
    isEqualExercise(exercise, currentExercise)
  )?.sets[currentSetIdx];

  const calculatedWeight =
    weight &&
    (is_completed || isNewProgramAdded
      ? weight
      : calculateWeight(pb, set?.weight));
  const calculatedReps =
    reps ?? calculateGoalReps(pb, calculatedWeight, rep_calculator);

  return {
    values: {
      weight: calculatedWeight || weight,
      reps: calculatedReps,
    },
    isCompleted: is_completed,
    id: currentSet?.id,
    day: day_index + 1,
    workoutIndex: workout_index + 1,
    exerciseName: exercise_name,
    exerciseId: exercise_id,
    exerciseIndex: exercise_index + 1,
    repCalculator: rep_calculator,
    pb: pb ?? null,
    pbIsCalculated: pbIsCalculated ?? true,
    set: {
      number: currentExercise?.sets.indexOf(currentSet) + 1,
      completed_at,
      goalReps: goal_reps,
      ...set,
      reps: currentExercise?.sets[currentSetIdx]?.reps,
    },
    notes,
    other_reps,
    other_reps_notes,
  };
};

/**
 *
 * @param {array} exercises
 * @param {number} currentDayIdx
 * @param prev get not next, but previous
 * @returns {number || false}
 */
export const getNextDayIdx = (exercises, currentDayIdx, prev = false) => {
  const isRestDay = (day) =>
    !day.every((exercise) => exercise.workout_index === null);

  const groupedByDay = groupByField(exercises, "day_index");
  const prevDays = groupedByDay.slice(0, currentDayIdx);
  const nextDays = groupedByDay.slice(currentDayIdx + 1);
  const remainingDays = prev ? prevDays.reverse() : nextDays;

  const nextNotRestDay = remainingDays.find((d) => !isRestDay(d));
  return nextNotRestDay ? groupedByDay.indexOf(nextNotRestDay) : false;
};

/**
 * @param {Array} exercises
 * @returns {{ exercise: Object, set: Object }}
 */
export const findInitial = (exercises, setPreselectInfo) => {
  if (setPreselectInfo) {
    const { exercise_array_index, set_array_index } = setPreselectInfo;
    const exercise = exercises[exercise_array_index];
    const set = exercise?.sets?.[set_array_index];

    return {
      exercise,
      set,
    };
  }

  return false;
};

/**
 * @param {object} exercise
 * @param {number} exercise.day_index
 * @param {number} exercise.workout_index
 * @param {number} exercise.exercise_index
 * @param {object} targetExercise
 * @param {number} targetExercise.day_index
 * @param {number} targetExercise.workout_index
 * @param {number} targetExercise.exercise_index
 * @returns {boolean}
 */
export const isSubsequentExercise = (exercise, targetExercise) => {
  return (
    exercise?.day_index > targetExercise?.day_index ||
    (exercise?.day_index === targetExercise?.day_index &&
      (exercise?.workout_index > targetExercise?.workout_index ||
        (exercise?.workout_index === targetExercise?.workout_index &&
          exercise?.exercise_index > targetExercise?.exercise_index)))
  );
};

/**
 * @param {object} exercise
 * @param {number} exercise.day_index
 * @param {number} exercise.workout_index
 * @param {number} exercise.exercise_index
 * @param {number} exercise.exercise_id
 * @param {object} targetExercise
 * @param {number} targetExercise.day_index
 * @param {number} targetExercise.workout_index
 * @param {number} targetExercise.exercise_index
 * @param {number} targetExercise.exercise_id
 * @returns {boolean}
 */
export const isEqualExercise = (exercise, targetExercise) => {
  return (
    exercise?.day_index === targetExercise?.day_index &&
    exercise?.workout_index === targetExercise?.workout_index &&
    exercise?.exercise_index === targetExercise?.exercise_index &&
    exercise?.exercise_id === targetExercise?.exercise_id
  );
};

export const roundWeightNumber = (weight) => {
  if (!weight) return weight;

  const lowerBound = Math.floor(weight);
  const fractionalPart = weight - lowerBound;

  return fractionalPart < 0.25
    ? lowerBound
    : fractionalPart < 0.75
    ? lowerBound + 0.5
    : lowerBound + 1;
};

/**
 * @param {object} exercise
 * @param {number} exercise.exercise_id
 * @param {object} targetExercise
 * @param {number} targetExercise.exercise_id
 * @returns {boolean}
 */
export const isSameTypeExercise = (exercise, targetExercise) => {
  return exercise?.exercise_id === targetExercise?.exercise_id;
};

export const isNotUniqueExercise = (idx, selectedExercise) =>
  idx === selectedExercise?.idx;

export const getEditExerciseModalMessage = (exerciseName) => (
  <>
    <div className="training-page__modal-title">Warning:</div>
    There are already instances of &quot;{exerciseName}&quot; in the program.
    Are you sure you want to create additional instances of &quot;{exerciseName}
    &quot;?
  </>
);

export const hasSubsequentSimilarExercises = (
  exercises,
  selectedExerciseInfo
) => {
  const { day_index, exercise_id } = selectedExerciseInfo;

  for (let i = day_index; i < exercises.length; i++) {
    if (exercises[i].exercise_id === exercise_id && i > day_index) return true;
  }

  return false;
};

/**
 *
 * @param {number} dayIdx
 * @param {number} programDaysCount
 * @returns {number} offset length
 */
export const getOffsetLength = ({
  dayIdx,
  programDaysCount,
  needLSizeOffset,
}) => {
  return needLSizeOffset
    ? WORKOUT_SCROLL_OFFSET_TOP_L
    : dayIdx === programDaysCount - 1
    ? NO_WORKOUT_SCROLL_OFFSET
    : WORKOUT_SCROLL_OFFSET_TOP_M;
};

export const getRemainingRepsMessage = (data) => {
  if (data?.total_reps) {
    return `${data.total_reps - data.remaining_reps} of ${
      data.total_reps
    } total reps complete`;
  }

  return null;
};

export const changeExerciseErrorHandler = (errors) => JSON.stringify(errors);

export const handleRemoveItem = ({
  itemType,
  data,
  exercisesForRemoval,
  dispatch,
  setExercisesForRemoval,
  itemIndex,
  isDayRemove,
  dayIdx,
}) => {
  const exercises = {
    stay: [],
    remove: [],
  };

  for (const exercise of data) {
    const ex = exercise[itemType] === itemIndex;
    const isNewExercise =
      exercise.exercise_id === null || exercise.id === undefined;

    const isRestDay = exercise.sets.length === 0 && Boolean(exercise.id);

    if (ex && isNewExercise && ((!isRestDay && isDayRemove) || !isDayRemove))
      continue;

    if (isDayRemove && ex) {
      exercises.remove.push(exercise);
    } else if (ex && !isDayRemove) {
      exercises.remove.push(exercise);
    } else {
      exercises.stay.push(exercise);
    }
  }

  if (exercises.remove.length) {
    dispatch(
      setExercisesForRemoval([...exercisesForRemoval, exercises.remove])
    );
  }

  return exercises.stay.length === 0 && !isDayRemove
    ? [
        {
          ...EMPTY_EXERCISE,
          day_index: dayIdx,
        },
      ]
    : getUpdatedArray(
        exercises.stay,
        (exercise) => exercise[itemType] > itemIndex,
        (exercise) => ({
          ...exercise,
          [itemType]: exercise[itemType] - 1,
        })
      );
};
export const checkIsSameExercise = (currentExercise, updatedExercise) => {
  return (
    currentExercise.day_index === updatedExercise.day_index &&
    currentExercise.workout_index === updatedExercise.workout_index &&
    currentExercise.exercise_index === updatedExercise.exercise_index
  );
};

export const checkIsRequestPossible = (reps, weight, repCalculator) => {
  if (!reps || !weight) return;

  const params = new URLSearchParams({ weight_percent: weight, reps: reps });
  return { params, isRepsBeyondOfRepCalculator: reps > MAX_REPS[repCalculator] };
};

export const totalRepsMessages = {
  firstSetIsBeyondOfRepsType: ({ max_set_properties, exercise_name, repCalculator }) => ({
    text: (
      <>
        <h3 className="training-page__modal-title">use total-rep method?</h3>
        <span>{`for ${exercise_name}, ${MAX_REPS[repCalculator]} reps is the maximum from which a calculated-max can be generated, 
        and would require a weight value of ${max_set_properties.weight_percent}% (or less). 
        do you want to edit the set, or use the total-rep method?`}</span>
      </>
    ),
    isQuestion: true,
  }),

  firstSetCanBeTotalRepsType: ({ reps, weight, max_set_properties }) => ({
    text: (
      <>
        <h3 className="training-page__modal-title">use total-rep method?</h3>
        <span>{`${max_set_properties.min_reps} ${
          max_set_properties.min_reps > 1 ? "reps" : "rep"
        } is the maximum possible with a ${weight}%-max weight. ${reps} reps requires a weight of ${
          max_set_properties.weight_percent
        }% (or less). 
        do you want to edit the set, or use the total-rep method?`}</span>
      </>
    ),
    isQuestion: true,
  }),

  otherSetIsBeyondOfRepsType: ({ reps, max_set_properties, exercise_name, repCalculator }) => ({
    text: (
      <>
        <h3 className="training-page__modal-title">warning</h3>
        <span>{`it is not possible to generate a calculated max for a ${reps} rep set. 
        ${MAX_REPS[repCalculator]} reps with a ${max_set_properties.weight_percent}% is the highest rep set possible on ${exercise_name}. 
        `}</span>
      </>
    ),
  }),

  otherSetCanBeTotalRepsType: ({ reps, weight, max_set_properties }) => ({
    text: (
      <>
        <h3 className="training-page__modal-title">warning</h3>
        <span>{`${max_set_properties.min_reps} ${
          max_set_properties.min_reps > 1 ? "reps" : "rep"
        } is the maximum possible with a ${weight}%-max weight. ${reps} reps requires a weight of ${
          max_set_properties.weight_percent
        }% (or less).`}</span>
      </>
    ),
  }),
};
